import React, { useState, useRef, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { FileDown, Upload, X, Send, User, Mail, Folder, FolderPlus, Trash2, Users, Eye } from 'lucide-react';
import type { PropertyListings } from '@/types/PropertyListings';
import { Spinner } from '@/components/ui/shadcn-io/spinner';
import api from '@/lib/axiosInstance';
import { toast } from 'sonner';
import Helper from '@/lib/helper';
import { useSelector } from 'react-redux';
import type { RootState } from '@/lib/redux/store';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Input } from '@/components/ui/input';
import { Separator } from '@/components/ui/separator';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faFolder } from '@fortawesome/free-solid-svg-icons'
import Alert from '@/components/Alert';
import ProposalConfiguration from './proposalConfiguration';

type FitMode = 'contain' | 'cover' | 'actual';

interface SectionImage {
    id: number | string;
    watermark: boolean;
    label?: string;
    fitMode?: FitMode;
    customHeight?: number;
    positionX?: number;
    positionY?: number;
    imageWidthPercent?: number;
    frameWidthPercent?: number;
}

const normalizeFitMode = (value: unknown): FitMode => {
    if (value === 'cover') return 'cover';
    if (value === 'actual') return 'actual';
    // 'contain', 'fitWidth', 'custom', undefined, etc → contain
    return 'contain';
};

const normalizeSections = (raw: any): any[] => {
    if (!Array.isArray(raw)) return [];
    return raw.map(section => ({
        ...section,
        images: Array.isArray(section.images)
            ? section.images.map((img: any) => ({ ...img, fitMode: normalizeFitMode(img?.fitMode) }))
            : section.images,
    }));
};

interface Feature {
    id: string;
    label: string;
    value: string;
}

interface FeatureCategory {
    id: string;
    label: string;
    features: Feature[];
}

interface Section {
    id: string;
    title: string;
    description: string;
    type?: string;
    images?: SectionImage[];
    editable?: boolean;
    deletable?: boolean;
    collapsed?: boolean;
    custom?: boolean;
    isNew?: boolean;
    featureCategories?: FeatureCategory[];
    useGoogleMaps?: boolean;
    mapZoom?: number;
    propertyTitle?: string;
    propertyDescription?: string;
}

interface _PropertyProposal {
    listing: PropertyListings;
    setListing: React.Dispatch<React.SetStateAction<PropertyListings | null>>;
}

interface ConsumerGroup {
    id: number;
    name: string;
    consumers: Array<{
        id?: number;
        consumer?: {
            id: number;
            name: string;
            email: string;
        };
        name?: string;
        email?: string;
    }>;
}

const PropertyProposal: React.FC<_PropertyProposal> = ({
    listing,
    setListing
}) => {
    const agent = useSelector((state: RootState) => state.auth.agent);
    const [previewProposal, setPreviewProposal] = useState<boolean>(false);
    const [downloading, setDownloading] = useState<boolean>(false);
    const [proposalPreviewHtml, setProposalPreviewHtml] = useState<string | null>(null);
    const [proposal, setProposal] = useState<any>(null);
    const [groups, setGroups] = useState<ConsumerGroup[]>([]);
    const [selectedGroup, setSelectedGroup] = useState<ConsumerGroup | null>(null);
    const [isGroupSheetOpen, setIsGroupSheetOpen] = useState<boolean>(false);
    const [isCreatingGroup, setIsCreatingGroup] = useState<boolean>(false);
    const [sending, setSending] = useState<boolean>(false);
    const [editingGroupId, setEditingGroupId] = useState<number | null>(null);
    const [editingGroupName, setEditingGroupName] = useState<string>('');
    const [groupEmailInput, setGroupEmailInput] = useState<string>('');
    const [tempGroupConsumers, setTempGroupConsumers] = useState<Array<{email: string, name?: string, isTemp: boolean}>>([]);
    const groupCsvInputRef = useRef<HTMLInputElement>(null);
    const [mediaChecked, setMediaChecked] = useState<boolean>(false);
    const [sections, setSections] = useState<Section[]>([
        {
            id: 'cover',
            title: 'Cover',
            description: 'Main cover image for the proposal',
            type: 'cover',
            images: [],
            editable: true
        },
        {
            id: 'specifications',
            title: 'Property Specifications',
            description: 'Key property details and features',
            type: 'specifications',
            featureCategories: [
                {
                    id: 'basic',
                    label: 'Features',
                    features: [
                        { id: 'builtUp', label: 'Built-up Area', value: `${Helper.formatNumber(Number(listing.builtUp))} sqft` },
                        { id: 'landArea', label: 'Land Area', value: `${Helper.formatNumber(Number(listing.landArea))} sqft` },
                        { id: 'bedrooms', label: 'Bedrooms', value: `${Helper.formatNumber(Number(listing.noOfBedrooms))}` },
                        { id: 'bathrooms', label: 'Bathrooms', value: `${Helper.formatNumber(Number(listing.noOfBathrooms))}` },
                        { id: 'parking', label: 'Parking', value: `${Helper.formatNumber(Number(listing.noOfParkingSpots))}` },
                        { id: 'furnishing', label: 'Furnishing', value: `${listing.furnishing?.label || 'N/A'}` },
                        { id: 'yearBuilt', label: 'Year Built', value: `${listing.yearOfCompletion || 'N/A'}` },
                        { id: 'availableDate', label: 'Available Date', value: `${listing.availableDate || 'N/A'}` },
                        { id: 'ceilingHeight', label: 'Ceiling Height', value: `${Helper.formatNumber(Number(listing.ceilingHeight))} ft` },
                        { id: 'floorLoading', label: 'Floor Loading', value: `${Helper.formatNumber(Number(listing.floorLoading))} t/m²` },
                        { id: 'cargoLifts', label: 'Cargo Lifts', value: `${Helper.formatNumber(Number(listing.totalCargoLifts))}` },
                        { id: 'passengerLifts', label: 'Passenger Lifts', value: `${Helper.formatNumber(Number(listing.totalPassengerLifts))}` },
                        { id: 'liftCapacity', label: 'Lift Capacity', value: `${Helper.formatNumber(Number(listing.maximumLiftCapacity))} kg` },
                        { id: 'electricitySupply', label: 'Electricity Supply', value: `${Helper.formatNumber(Number(listing.electricitySupply))} amp` },
                        { id: 'price', label: 'Price', value: listing.forTender ? `Price on request` : `RM${Helper.formatWithDecimal(Number(listing.price))}` }
                    ]
                }
            ],
            editable: true,
            deletable: false
        },
        {
            id: 'overview',
            title: 'Overview',
            description: '',
            propertyTitle: listing.headline,
            propertyDescription: listing.description,
            type: 'overview',
            editable: true,
            deletable: true
        },
        {
            id: 'gallery',
            title: 'Gallery',
            description: '',
            images: [],
            editable: true,
            deletable: false
        },
        {
            id: 'floorPlan',
            title: 'Floor Plans',
            description: '',
            images: [],
            editable: true,
            deletable: true
        },
        {
            id: 'measurement',
            title: 'Measurements',
            description: '',
            images: [],
            editable: true,
            deletable: true
        },
        {
            id: 'location',
            title: 'Location',
            description: '',
            useGoogleMaps: false,
            mapZoom: 15,
            images: [],
            editable: true,
            deletable: true
        }
    ]);

    const getMedias = async () => {
        try {
            const response = await api.get(`/listings/${listing.id}/medias`);
            if(!response.data.status) {
                throw new Error(response.data.error);
            }
            const medias = response.data.medias;
            listing.medias = medias;
            setListing({...listing});
            setMediaChecked(true);
        } catch (err) {
            const error = Helper.errorHandler(err);
            toast.error(error);
        }
    }

    const isValidEmail = (email: string): boolean => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    };

    const handleGroupEmailSubmit = (e?: React.KeyboardEvent<HTMLInputElement>) => {
        if (e && e.key !== 'Enter') return;
        if (!selectedGroup) return;
        
        const email = groupEmailInput.trim().toLowerCase();
        
        if (!email) return;
        
        if (!isValidEmail(email)) {
            toast.error('Please enter a valid email address');
            return;
        }

        const isDuplicateInGroup = selectedGroup.consumers.some(m => 
            (m.consumer?.email || m.email)?.toLowerCase() === email
        );
        
        const isDuplicateInTemp = tempGroupConsumers.some(m => m.email.toLowerCase() === email);
        
        if (isDuplicateInGroup || isDuplicateInTemp) {
            toast.error('This email is already in the group');
            return;
        }

        setTempGroupConsumers(prev => [...prev, {
            email: email,
            isTemp: true
        }]);
        setGroupEmailInput('');
    };

    const addConsumersToGroup = async (groupId: number) => {
        if (tempGroupConsumers.length === 0) {
            toast.error('No consumers to add');
            return;
        }

        try {
            setSending(true);
            const response = await api.post(`/listings/${listing.id}/proposals/groups/${groupId}/consumers/bulk`, {
                consumers: tempGroupConsumers.map(c => ({ email: c.email, name: c.name }))
            }, {
                headers: {
                    'Content-Type': 'application/json',
                }
            });
            if(!response.data.status) {
                throw new Error(response.data.error);
            }
            const createdConsumers = response.data.consumers || [];
            setGroups(prev => prev.map(g => 
                g.id === groupId 
                    ? { ...g, consumers: [...g.consumers, ...createdConsumers] }
                    : g
            ));
            if (selectedGroup?.id === groupId) {
                setSelectedGroup({ 
                    ...selectedGroup, 
                    consumers: [...selectedGroup.consumers, ...createdConsumers] 
                });
            }
            setTempGroupConsumers([]);
            if (response.data.failed?.length) {
                toast.warning(`Sent ${createdConsumers.length} proposal(s), ${response.data.failed.length} failed.`);
            } else {
                toast.success(`Sent ${createdConsumers.length} proposal(s) in batches`);
            }
        } catch (err: any) {
            const error = Helper.errorHandler(err);
            toast.error(error);
        } finally {
            setSending(false);
        }
    };

    const [savedSectionsJson, setSavedSectionsJson] = useState<string>('');
    const [isSaving, setIsSaving] = useState<boolean>(false);

    const computeSectionsJson = (s: Section[]) => JSON.stringify(s);
    // Dirty whenever the in-memory sections differ from the last persisted snapshot.
    // For listings without a saved proposal yet, the snapshot is empty so any tweak shows the save bar.
    const isDirty = computeSectionsJson(sections) !== savedSectionsJson;

    const getProposal = async () => {
        try {
            const response = await api.get(`/listings/${listing.id}/proposals`);
            if (!response.data.status) {
                throw new Error(response.data.error);
            }
            setProposal(response.data.proposal);
            if(response.data.proposal) {
                const next = normalizeSections(JSON.parse(response.data.proposal.templateJson));
                setSections(next);
                setSavedSectionsJson(computeSectionsJson(next as Section[]));
            }
        } catch (err: any) {
            const error = Helper.errorHandler(err);
            toast.error(error);
        }
    };

    const saveProposal = async () => {
        try {
            setIsSaving(true);
            const response = await api.post(`/listings/${listing.id}/proposals`, {
                sections,
            }, {
                headers: { 'Content-Type': 'application/json' },
            });
            if (!response.data.status) throw new Error(response.data.error);
            setProposal(response.data.proposal);
            if (response.data.proposal) {
                const next = normalizeSections(JSON.parse(response.data.proposal.templateJson));
                setSections(next);
                setSavedSectionsJson(computeSectionsJson(next as Section[]));
            } else {
                setSavedSectionsJson(computeSectionsJson(sections));
            }
            toast.success('Saved proposal!');
        } catch (err: any) {
            toast.error(Helper.errorHandler(err));
        } finally {
            setIsSaving(false);
        }
    };

    const getGroups = async () => {
        try {
            const response = await api.get(`/listings/${listing.id}/proposals/groups`);
            if(!response.data.status) {
                throw new Error(response.data.error);
            }
            setGroups(response.data.groups || []);
        } catch (err: any) {
            const error = Helper.errorHandler(err);
            toast.error(error);
        }
    };

    const createGroup = async () => {
        setIsCreatingGroup(true);
        try {
            const response = await api.post(`/listings/${listing.id}/proposals/groups`, {
                name: `New Folder ${groups.length + 1}`
            });
            if(!response.data.status) {
                throw new Error(response.data.error);
            }
            setGroups(prev => [...prev, response.data.group]);
            setEditingGroupId(response.data.group.id);
            setEditingGroupName(response.data.group.name);
        } catch (err: any) {
            const error = Helper.errorHandler(err);
            toast.error(error);
        } finally {
            setIsCreatingGroup(false);
        }
    };

    const updateGroupName = async (groupId: number, newName: string) => {
        if (!newName.trim()) {
            toast.error('Please enter a group name');
            return;
        }

        try {
            const response = await api.post(`/listings/${listing.id}/proposals/groups/${groupId}`, {
                name: newName.trim()
            });
            if(!response.data.status) {
                throw new Error(response.data.error);
            }
            setGroups(prev => prev.map(g => g.id === groupId ? { ...g, name: newName.trim() } : g));
            setEditingGroupId(null);
            setEditingGroupName('');
        } catch (err: any) {
            const error = Helper.errorHandler(err);
            toast.error(error);
        }
    };

    const deleteGroup = async (groupId: number) => {
        try {
            const response = await api.delete(`/listings/${listing.id}/proposals/groups/${groupId}`);
            if(!response.data.status) {
                throw new Error(response.data.error);
            }
            setGroups(prev => prev.filter(g => g.id !== groupId));
            if (selectedGroup?.id === groupId) {
                setSelectedGroup(null);
                setIsGroupSheetOpen(false);
            }
        } catch (err: any) {
            const error = Helper.errorHandler(err);
            toast.error(error);
        }
    };

    const removeConsumerFromGroup = async (groupId: number, consumerEmail: string) => {
        try {
            const response = await api.delete(`/listings/${listing.id}/proposals/groups/${groupId}/consumers?email=${consumerEmail}`);
            if(!response.data.status) {
                throw new Error(response.data.error);
            }
            setGroups(prev => prev.map(g => 
                g.id === groupId 
                    ? { ...g, consumers: g.consumers.filter(m => (m.consumer?.email || m.email) !== consumerEmail) }
                    : g
            ));
            if (selectedGroup?.id === groupId) {
                setSelectedGroup({ 
                    ...selectedGroup, 
                    consumers: selectedGroup.consumers.filter(m => (m.consumer?.email || m.email) !== consumerEmail) 
                });
            }
        } catch (err: any) {
            const error = Helper.errorHandler(err);
            toast.error(error);
        }
    };

    const removeTempConsumer = (email: string) => {
        setTempGroupConsumers(prev => prev.filter(c => c.email !== email));
    };

    const parseCsvFile = (file: File, isGroupUpload: boolean = false): Promise<Array<{name: string, email: string}>> => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = (e) => {
                try {
                    const text = e.target?.result as string;
                    const lines = text.split('\n');
                    const consumers: Array<{name: string, email: string}> = [];
                    const seenEmails = new Set<string>();

                    for (let i = 1; i < lines.length; i++) {
                        const line = lines[i].trim();
                        if (!line) continue;

                        const data = line.split('\t');
                        if (!data[0]) {
                            continue;
                        }
                        const dt = data[0].split(',');
                        if(dt.length >= 2) {
                            const name = dt[0].trim();
                            const email = dt[1].trim().toLowerCase();
    
                            if (name && email && !seenEmails.has(email)) {
                                seenEmails.add(email);
                                consumers.push({ name, email });
                            }
                        }
                    }
                    resolve(consumers);
                } catch (err) {
                    reject(err);
                }
            };
            reader.onerror = () => reject(new Error('Failed to read file'));
            reader.readAsText(file);
        });
    };

    // Add this function to handle CSV template download
    const downloadCsvTemplate = () => {
        const csvContent = "Name,Email\nJohn Doe,john@example.com\nJane Smith,jane@example.com";
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', 'consumer_template.csv');
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const handleGroupCsvUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file || !selectedGroup) return;

        try {
            const parsedConsumers = await parseCsvFile(file, true);
            
            if (parsedConsumers.length === 0) {
                toast.error('No valid consumers found in CSV');
                return;
            }

            const existingEmails = new Set([
                ...selectedGroup.consumers.map(m => (m.consumer?.email || m.email)?.toLowerCase()),
                ...tempGroupConsumers.map(m => m.email.toLowerCase())
            ]);
            
            const newConsumers = parsedConsumers.filter(c => !existingEmails.has(c.email));

            if (newConsumers.length === 0) {
                toast.error('All consumers from CSV are already in the group');
                return;
            }

            setTempGroupConsumers(prev => [...prev, ...newConsumers.map(c => ({
                email: c.email,
                name: c.name,
                isTemp: true
            }))]);

        } catch (err: any) {
            const error = Helper.errorHandler(err);
            toast.error(error);
        }

        if (groupCsvInputRef.current) {
            groupCsvInputRef.current.value = '';
        }
    };

    const generatePdf = async () => {
        try {
            setDownloading(true);
            const response = await api.get(`/listings/${listing.id}/proposals/export`, {
                responseType: 'blob',
            });
    
            const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `listing-${listing.id}.pdf`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (err: any) {
            const error = Helper.errorHandler(err);
            toast.error(error);
        } finally {
            setDownloading(false);
        }
    };

    const getProposalPreviewTemplate = async (opts?: { openDialog?: boolean }) => {
        try {
            const response = await api.post(`/listings/${listing.id}/proposals/preview`, {
                sections,
            }, {
                headers: {
                    'Content-Type': 'application/json',
                },
            });
            if (!response.data.status) {
                throw new Error(response.data.error);
            }

            // The template lays every page out as a fixed A4 box (same geometry the PDF
            // uses), so it is rendered as-is — no width/height overrides.
            setProposalPreviewHtml(response.data.html);
            if (opts?.openDialog) {
                setPreviewProposal(true);
            }
        } catch (err: any) {
            const error = Helper.errorHandler(err);
            toast.error(error);
        }
    };
    
    useEffect(() => {
        // Capture the initial sections snapshot so the dirty-state save bar only appears after edits.
        setSavedSectionsJson(prev => prev || computeSectionsJson(sections));
        getProposal();
        getGroups();
    }, []);

    useEffect(() => {
        if(listing && listing.createdBy !== agent.id) {
            getProposalPreviewTemplate();
        }

        if(listing && !mediaChecked) {
            getMedias();
        }
    }, [listing]);

    useEffect(() => {
        if (!listing?.id || !mediaChecked) return;
        void getProposalPreviewTemplate();
    }, [mediaChecked, listing?.id]);

    return (
        <div className="min-h-screen bg-background">
            <div className="max-w-6xl mx-auto">
                <div className={`space-y-6 ${listing.createdBy === agent.id && isDirty ? 'pb-28' : ''}`}>
                    <Dialog open={previewProposal} onOpenChange={setPreviewProposal}>
                        <DialogContent className="flex max-h-[95vh] max-w-[95vw] flex-col overflow-hidden gap-0 p-0">
                            <iframe
                                title="Proposal preview"
                                srcDoc={proposalPreviewHtml ?? ''}
                                className="min-h-[85vh] w-full flex-1 border-0 bg-white"
                                sandbox="allow-same-origin allow-scripts allow-popups allow-forms allow-downloads"
                            />
                        </DialogContent>
                    </Dialog>
                    <Sheet open={isGroupSheetOpen} onOpenChange={setIsGroupSheetOpen}>
                        <SheetContent className="w-full sm:max-w-xl overflow-y-auto p-3">
                            <SheetHeader>
                                <SheetTitle className="flex items-center gap-2 text-muted-foreground">
                                    <Folder className="h-5 w-5 text-muted-foreground" />
                                    {selectedGroup ? selectedGroup.name : 'Manage Group'}
                                </SheetTitle>
                                <SheetDescription>
                                    {
                                        selectedGroup 
                                            ? 'Add consumers and send proposals to this group'
                                            : 'Manage group consumers'
                                    }
                                </SheetDescription>
                            </SheetHeader>
                            <div className="mt-6 space-y-4">
                                {
                                    selectedGroup && (
                                        <>
                                            <div className="space-y-2">
                                                <Label>Add Consumer by Email</Label>
                                                <div className="flex gap-2">
                                                    <Input
                                                    placeholder="Enter email address..."
                                                    value={groupEmailInput}
                                                    onChange={(e) => setGroupEmailInput(e.target.value)}
                                                    onKeyPress={handleGroupEmailSubmit}
                                                    className="flex-1"/>
                                                    <Button
                                                    size="sm"
                                                    onClick={() => handleGroupEmailSubmit()}
                                                    disabled={!groupEmailInput.trim()}>
                                                        Add
                                                    </Button>
                                                </div>
                                                <p className="text-xs text-muted-foreground">Press Enter or click Add to submit</p>
                                                <input
                                                ref={groupCsvInputRef}
                                                type="file"
                                                accept=".csv,.txt"
                                                onChange={handleGroupCsvUpload}
                                                className="hidden" />
                                                <div className='flex items-center gap-2'>
                                                    <Button
                                                    variant="outline"
                                                    size="sm"
                                                    onClick={() => groupCsvInputRef.current?.click()}>
                                                        <Upload className="h-4 w-4 mr-2" />
                                                        Upload CSV
                                                    </Button>
                                                    <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={downloadCsvTemplate}
                                                    className="bg-gray-100 hover:bg-gray-200 text-xs">
                                                        Template
                                                    </Button>
                                                </div>
                                            </div>
                                            {
                                                tempGroupConsumers.length > 0 && (
                                                    <>
                                                        <Separator />
                                                        <div className="space-y-2">
                                                            <div className="flex items-center justify-between">
                                                                <Label>Pending Consumers ({tempGroupConsumers.length})</Label>
                                                                <Button
                                                                size="sm"
                                                                onClick={() => addConsumersToGroup(selectedGroup.id)}
                                                                disabled={listing.closed === 1 || sending}>
                                                                    {
                                                                        sending ? (
                                                                            <Spinner />
                                                                        ) : (
                                                                            <Send className="h-4 w-4 mr-2" />
                                                                        )
                                                                    }
                                                                    Send All
                                                                </Button>
                                                            </div>
                                                            <div className="space-y-2 max-h-48 overflow-y-auto">
                                                                {
                                                                    tempGroupConsumers.map((consumer, idx) => (
                                                                        <div
                                                                        key={idx}
                                                                        className="flex items-center justify-between p-3 bg-yellow-50 rounded-lg border border-yellow-200"
                                                                        >
                                                                            <div className="flex items-center gap-3 flex-1 min-w-0">
                                                                                <div className="flex-shrink-0 w-10 h-10 bg-yellow-100 rounded-full flex items-center justify-center">
                                                                                    <User className="h-5 w-5 text-yellow-600" />
                                                                                </div>
                                                                                <div className="flex-1 min-w-0">
                                                                                    <p className="text-sm font-medium truncate">
                                                                                        {consumer.name || consumer.email}
                                                                                    </p>
                                                                                    <div className="flex items-center gap-2 text-xs text-muted-foreground mt-0.5">
                                                                                        <Mail className="h-3 w-3" />
                                                                                        <span className="truncate">{consumer.email}</span>
                                                                                    </div>
                                                                                </div>
                                                                            </div>
                                                                            <Button
                                                                            variant="ghost"
                                                                            size="sm"
                                                                            onClick={() => removeTempConsumer(consumer.email)}
                                                                            className="text-destructive hover:text-destructive hover:bg-destructive/10">
                                                                                <X className="h-4 w-4" />
                                                                            </Button>
                                                                        </div>
                                                                    ))
                                                                }
                                                            </div>
                                                        </div>
                                                    </>
                                                )
                                            }
                                            <Separator />
                                            <div className="space-y-2">
                                                <div className="flex items-center justify-between">
                                                    <Label>Consumers ({selectedGroup.consumers.length})</Label>
                                                </div>
                                                {
                                                    selectedGroup.consumers.length === 0 ? (
                                                        <div className="text-center py-8 text-muted-foreground border border-dashed rounded-lg">
                                                            <Users className="h-8 w-8 mx-auto mb-2 opacity-50" />
                                                            <p className="text-sm">No consumers yet</p>
                                                            <p className="text-xs mt-1">Add email or upload CSV to add consumers</p>
                                                        </div>
                                                    ) : (
                                                        <div className="space-y-2">
                                                            {selectedGroup.consumers.map((member) => {
                                                                const memberEmail = member.consumer?.email || member.email || '';
                                                                const memberName = member.consumer?.name || member.name || memberEmail;
                                                                
                                                                return (
                                                                    <div
                                                                    key={member.id || memberEmail}
                                                                    className="flex items-center justify-between p-3 bg-muted/30 rounded-lg border border-border"
                                                                    >
                                                                        <div className="flex items-center gap-3 flex-1 min-w-0">
                                                                            <div className="flex-shrink-0 w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
                                                                                <User className="h-5 w-5 text-primary" />
                                                                            </div>
                                                                            <div className="flex-1 min-w-0">
                                                                                <p className="text-sm font-medium truncate">
                                                                                    {memberName}
                                                                                </p>
                                                                                <div className="flex items-center gap-2 text-xs text-muted-foreground mt-0.5">
                                                                                    <Mail className="h-3 w-3" />
                                                                                    <span className="truncate">{memberEmail}</span>
                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                        <Button
                                                                        variant="ghost"
                                                                        size="sm"
                                                                        onClick={() => removeConsumerFromGroup(selectedGroup.id, memberEmail)}
                                                                        className="text-destructive hover:text-destructive hover:bg-destructive/10">
                                                                            <X className="h-4 w-4" />
                                                                        </Button>
                                                                    </div>
                                                                );
                                                            })}
                                                        </div>
                                                    )
                                                }
                                            </div>
                                        </>
                                    )
                                }
                            </div>
                        </SheetContent>
                    </Sheet>
                    {
                        proposal && (
                            <div className="space-y-4">
                                <div className="flex items-center justify-between">
                                    <Label className="text-sm font-medium">Consumer Groups</Label>
                                    <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={createGroup}
                                    disabled={isCreatingGroup || listing.closed === 1}>
                                        <FolderPlus className="h-4 w-4 mr-2" />
                                        New Folder
                                    </Button>
                                </div>
                                {
                                    groups.length > 0 && (
                                        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-4">
                                            {groups.map((group) => (
                                                <div
                                                key={group.id}
                                                className="relative group/folder">
                                                    <div
                                                    className="flex flex-col items-center justify-center text-muted-foreground">
                                                        <FontAwesomeIcon
                                                        icon={faFolder}
                                                        className="text-yellow-500 text-4xl cursor-pointer"
                                                        onClick={() => {
                                                            setSelectedGroup(group);
                                                            setIsGroupSheetOpen(true);
                                                        }} />
                                                    </div>
                                                    <div className="mt-2 flex justify-center items-center">
                                                        <Input
                                                        value={editingGroupId === group.id ? editingGroupName : group.name}
                                                        onChange={(e) => {
                                                            if (editingGroupId === group.id) {
                                                                setEditingGroupName(e.target.value);
                                                            }
                                                        }}
                                                        onFocus={() => {
                                                            setEditingGroupId(group.id);
                                                            setEditingGroupName(group.name);
                                                        }}
                                                        onBlur={() => {
                                                            if (editingGroupId === group.id && editingGroupName.trim() && editingGroupName !== group.name) {
                                                                updateGroupName(group.id, editingGroupName);
                                                            } else {
                                                                setEditingGroupId(null);
                                                                setEditingGroupName('');
                                                            }
                                                        }}
                                                        onKeyPress={(e) => {
                                                            if (e.key === 'Enter' && editingGroupId === group.id) {
                                                                if (editingGroupName.trim() && editingGroupName !== group.name) {
                                                                    updateGroupName(group.id, editingGroupName);
                                                                } else {
                                                                    setEditingGroupId(null);
                                                                    setEditingGroupName('');
                                                                }
                                                            }
                                                        }}
                                                        className="shadow-none h-7 text-sm text-center border-0 bg-transparent focus-visible:ring-0 focus-visible:ring-offset-0 px-1"
                                                        disabled={listing.closed === 1}/>
                                                        <Alert
                                                        trigger={
                                                            <Button
                                                                size="sm"
                                                                variant="ghost"
                                                                onClick={(e) => e.stopPropagation()}
                                                                className="h-7 w-7 p-0 text-destructive"
                                                                disabled={listing.closed === 1}
                                                            >
                                                                <Trash2 className="h-3.5 w-3.5 text-red" />
                                                            </Button>
                                                        }
                                                        title="Delete Folder"
                                                        description={`Are you sure you want to delete the folder "${group.name}"? This action cannot be undone.`}
                                                        confirmLabel="Delete"
                                                        cancelLabel="Cancel"
                                                        onConfirm={async () => {
                                                            await deleteGroup(group.id);
                                                        }}/>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )
                                }
                            </div>
                        )
                    }
                    {
                        proposal && agent.id !== listing.createdBy && (
                            <div className="space-y-4">
                                <div className="flex justify-end">
                                    <Button 
                                    size="sm"
                                    onClick={generatePdf}
                                    disabled={downloading}>
                                        <FileDown size={16} className="mr-2" />
                                        Generate PDF
                                        {downloading && <Spinner />}
                                    </Button>
                                </div>
                                <p className="text-right text-xs text-muted-foreground">
                                    Large images slow PDF generation; compress assets where possible.
                                </p>
                                <Card className="border-border">
                                    <CardContent className="p-4 bg-gray-100">
                                        <div className="mx-auto" style={{ maxWidth: 'calc(210mm + 32px)' }}>
                                            <div 
                                            className="overflow-auto"
                                            style={{ 
                                                height: 'calc(100vh - 250px)',
                                                minHeight: '600px'
                                            }}>
                                            <iframe
                                                title="Proposal preview"
                                                srcDoc={proposalPreviewHtml ?? ''}
                                                className="h-full min-h-[600px] w-full border-0 bg-white"
                                                sandbox="allow-same-origin allow-scripts allow-popups allow-forms allow-downloads"
                                            />
                                            </div>
                                        </div>
                                    </CardContent>
                                </Card>
                            </div>
                        )
                    }
                    {
                        agent.id === listing.createdBy && (
                            <div className="space-y-4">
                                <div className="flex justify-end gap-2">
                                    <Button variant="outline" size="sm" onClick={() => void getProposalPreviewTemplate({ openDialog: true })}>
                                        <Eye size={16} className="mr-2" />
                                        Preview
                                    </Button>
                                    {
                                        proposal && (
                                            <Button 
                                            size="sm"
                                            onClick={generatePdf}
                                            disabled={downloading}>
                                                <FileDown size={16} className="mr-2" />
                                                Generate PDF
                                                {downloading && <Spinner />}
                                            </Button>
                                        )
                                    }
                                </div>
                                <p className="text-right text-xs text-muted-foreground">
                                    PDF generation time depends on image size and count. Use reasonably sized photos in proposal configuration to keep exports faster.
                                </p>
                            </div>
                        )
                    }
                    {
                        listing.createdBy === agent.id && (
                            <ProposalConfiguration
                            listing={listing}
                            proposal={proposal}
                            setProposal={setProposal}
                            sections={sections}
                            setSections={setSections}/>
                        )
                    }
                </div>
            </div>
            {listing.createdBy === agent.id && isDirty && (
                <div className="fixed inset-x-0 bottom-0 z-50 border-t border-border bg-background/95 px-4 py-3 backdrop-blur shadow-[0_-4px_12px_rgba(15,23,42,0.06)]">
                    <div className="mx-auto flex max-w-6xl items-center justify-between gap-3">
                        <span className="text-sm text-muted-foreground">You have unsaved changes to this proposal.</span>
                        <div className="flex items-center gap-2">
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => {
                                    if (savedSectionsJson) {
                                        try {
                                            setSections(normalizeSections(JSON.parse(savedSectionsJson)));
                                        } catch {
                                            // ignore
                                        }
                                    }
                                }}
                                disabled={isSaving}
                            >
                                Discard
                            </Button>
                            <Button size="sm" onClick={saveProposal} disabled={isSaving}>
                                {isSaving ? <Spinner /> : 'Save changes'}
                            </Button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default PropertyProposal;
